import { useEffect, useState } from "react";
import AddIcon from '@mui/icons-material/Add';

import { gridApiKeyStore } from "../../../../../api/grid-api-key-store";
import { municipalityApiKeyStore } from "../../../../../api/masters/municipality-api-key-store";
import { SiTrackGrid } from "../../../../../components/si-track-grid/si-track-grid";
import { useLoadData } from "../../../../../hooks/useLoadData";
import { useSaveData } from "../../../../../hooks/useSaveData";
import { ApiResponse } from "../../../../../models/api-response";
import { GridColumnConfig, GridPreference } from "../../../../../models/gridCoumnConfig";
import { UserPermissionName } from "../../../../../models/user-permission-name";
import { noteColumns } from "./note-grid-columns";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../../../../hooks/use-debounce";
import { CreateNoteDialog } from "../../create_municipality/crate-note.dialog";
import { GuardedComponent } from "../../../../../components/guarded-component";
import { Button } from "@mui/material";

interface CellProps {
    noteData: any;
}

interface CustomGridCellProps {
    field: string;
}

interface MunicipalityNoteTab {
    municipalityID: number;
}
export const MunicipalityNoteTab = ({ municipalityID }: MunicipalityNoteTab) => {

    const [editItem, setEditItem] = useState<any>();

    const handleCreateNote = () => {
        setEditItem({ municipalityID: municipalityID });
    }

    const handleEditNote = (editNote: any) => {
        setEditItem({ ...editNote, municipalityID: municipalityID });
    }

    const handleNoteData = (noteValue: any) => {
    }

    // -------------------------------------------------Grid Layout------------------------------

    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout,
    } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Municipality Note', 'Municipality Note'));

    const {
        isLoading: isLoadingNote,
        isFetching,
        data: contacts,
        refetch,
    } = useLoadData<ApiResponse<any[]>>(municipalityApiKeyStore.getMunicipalityNotes({ municipalityID }));

    const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredContacts, setFilteredContacts] = useState<any[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredContactList = contacts?.data?.filter((file) =>
            Object.values(file).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredContacts(filteredContactList || []);
    }, [debouncedSearchTerm, contacts?.data]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: 'Municipality Note',
            pageName: 'Municipality Note',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: 'Municipality Note',
            pageName: 'Municipality Note',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    //-------------------------------------------------------------------

    const DescriptionCell = ({ noteData }: CellProps) => {
        const description = noteData?.note;

        return (
            <td>
                <div
                    className="note-html"
                    dangerouslySetInnerHTML={{ __html: description }}
                />
                <style>
                    {`.note-html a {color: blue;text-decoration: none;}
                      .note-html a:hover {text-decoration: underline;}`
                    }
                </style>
            </td >
        );
    };

    const CustomGridCell = ({ field }: CustomGridCellProps) => {
        if (field === 'note') {
            return (dataItem: any) => {
                return <DescriptionCell noteData={dataItem.dataItem} />;
            };
        }
    };

    return (
        <div className="borderTop">
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_NOTE]}>
                <Button
                    className="innerTab-button"
                    size="small"
                    variant="contained"
                    disableElevation={true}
                    startIcon={<AddIcon />}
                    onClick={handleCreateNote}
                >
                    Add Notes
                </Button>
            </GuardedComponent>
            <SiTrackGrid
                fileTitle='municipality_note'
                columns={noteColumns}
                dataItemKey="municipalityNoteMappingID"
                customCell={(field) =>
                    CustomGridCell({
                        field,
                    })
                }
                onEdit={handleEditNote}
                deleteMutationConfig={(item) => municipalityApiKeyStore.deleteMunicipalityNote(item.municipalityNoteMappingID)}
                actionPermissions={{
                    edit: UserPermissionName?.EDIT_NOTE,
                    delete: UserPermissionName?.DELETE_NOTE,
                    export: UserPermissionName.DOWNLOAD_GRID,
                }}

                isBusy={isLoadingNote || isFetching || isLoadingGridLayout}
                // -----------------Grid Layout-------
                dataItems={filteredContacts || []}
                reorderable
                handleSearch={handleInputChange}
                handleSaveLayout={handleSaveLayout}
                isSaveLayout={true}
                gridConfigFromApi={gridConfig}
                handleResetLayout={handleResetLayout}
            // -----------------Grid Layout-------
            />
            {editItem && (
                <CreateNoteDialog
                    title={editItem?.municipalityNoteMappingID ? "UPDATE NOTE" : "CREATE NOTE"}
                    config={{
                        saveConfig: editItem.municipalityNoteMappingID
                            ? municipalityApiKeyStore.updateMunicipalityNote()
                            : municipalityApiKeyStore.createMunicipalityNote(),
                        invalidateRoute: () => municipalityApiKeyStore.getMunicipalityNotes({ municipalityID }),
                    }}
                    dataItem={editItem}
                    setNoteValue={handleNoteData}
                    onClosed={() => setEditItem(undefined)} />
            )}
        </div>
    );
}